// src/pages/AddMemberPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout'; // wrapped with layout in same folder

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AddMemberPage = () => {
  const navigate = useNavigate();

  // Current step (1-4)
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      idNumber: '',
      gender: '',
      phoneNumber: '',
      email: '',
      dateOfBirth: '',
      physicalAddress: '',
      city: '',
      homeCounty: '',
      employmentStatus: '',
      employerName: ''
    },
    saccoInfo: {
      memberId: '',
      dateJoined: new Date().toISOString().split('T')[0],
      membershipType: 'Regular',
      status: 'Active',
      department: '',
      payrollNumber: ''
    },
    nextOfKin: {
      name: '',
      phoneNumber: '',
      relationship: '',
      idNumber: '',
      physicalAddress: '',
      benefitPercentage: ''
    }
  });

  // Other next of kin list (additional beneficiaries)
  const [otherNextOfKin, setOtherNextOfKin] = useState([]);

  // Errors
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdMember, setCreatedMember] = useState(null);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');

    // Format as +254 XXX XXX XXX
    if (cleaned.startsWith('254')) {
      const formatted = '+254' + cleaned.substring(3);
      return formatted.substring(0, 13); // +254 + 9 digits
    } else if (cleaned.startsWith('0')) {
      return '+254' + cleaned.substring(1, 10);
    } else if (cleaned.length > 0) {
      return '+254' + cleaned.substring(0, 9);
    }
    return '+254';
  };

  // Format currency
  const formatCurrency = (value) => {
    const number = value.replace(/[^0-9]/g, '');
    return number ? parseInt(number).toLocaleString() : '';
  };

  // Handle input change
  const handleChange = (section, field, value) => {
    // Special formatting
    if (field === 'phoneNumber') {
      value = formatPhoneNumber(value);
    } else if (field === 'idNumber') {
      value = value.toUpperCase().trim();
    }

    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Clear error for this field
    if (errors[`${section}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  // Other Next of Kin handlers
  const addOtherKin = () => setOtherNextOfKin(prev => [...prev, { name: '', relationship: '', benefitPercentage: '' }]);
  const removeOtherKin = (idx) => setOtherNextOfKin(prev => prev.filter((_, i) => i !== idx));
  const handleOtherKinChange = (idx, field, value) => {
    setOtherNextOfKin(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  // Validate step
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      // Personal Info validation
      if (!formData.personalInfo.fullName.trim()) {
        newErrors['personalInfo.fullName'] = 'Full name is required';
      } else if (formData.personalInfo.fullName.length < 3) {
        newErrors['personalInfo.fullName'] = 'Name must be at least 3 characters';
      } else if (!/^[A-Za-z\s\-']+$/.test(formData.personalInfo.fullName)) {
        newErrors['personalInfo.fullName'] = 'Name must contain only letters';
      }

      if (!formData.personalInfo.idNumber.trim()) {
        newErrors['personalInfo.idNumber'] = 'ID Number is required';
      } else if (formData.personalInfo.idNumber.length < 7) {
        newErrors['personalInfo.idNumber'] = 'ID Number must be at least 7 characters';
      }

      if (!formData.personalInfo.phoneNumber) {
        newErrors['personalInfo.phoneNumber'] = 'Phone number is required';
      } else if (!/^\+254[0-9]{9}$/.test(formData.personalInfo.phoneNumber)) {
        newErrors['personalInfo.phoneNumber'] = 'Invalid phone number format';
      }

      if (formData.personalInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalInfo.email)) {
        newErrors['personalInfo.email'] = 'Invalid email format';
      }

      if (!formData.personalInfo.dateOfBirth) {
        newErrors['personalInfo.dateOfBirth'] = 'Date of birth is required';
      } else {
        const birthDate = new Date(formData.personalInfo.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 18) {
          newErrors['personalInfo.dateOfBirth'] = 'Member must be at least 18 years old';
        }
      }

      if (!formData.personalInfo.physicalAddress.trim()) {
        newErrors['personalInfo.physicalAddress'] = 'Address is required';
      } else if (formData.personalInfo.physicalAddress.length < 10) {
        newErrors['personalInfo.physicalAddress'] = 'Address must be at least 10 characters';
      }

      if (!formData.personalInfo.employmentStatus) {
        newErrors['personalInfo.employmentStatus'] = 'Employment area is required';
      }
    }

    if (step === 2) {
      // SACCO Info validation
      if (!formData.saccoInfo.dateJoined) {
        newErrors['saccoInfo.dateJoined'] = 'Date joined is required';
      }
    }

    if (step === 3) {
      // Next of Kin validation
      if (!formData.nextOfKin.name.trim()) {
        newErrors['nextOfKin.name'] = 'Next of kin name is required';
      } else if (formData.nextOfKin.name.length < 3) {
        newErrors['nextOfKin.name'] = 'Name must be at least 3 characters';
      }
      if (!formData.nextOfKin.relationship) {
        newErrors['nextOfKin.relationship'] = 'Relationship is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle back
  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle submit
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('hopeSaccoToken');

      // Prepare data
      const submitData = {
        memberId: formData.saccoInfo.memberId,
        personalInfo: {
          ...formData.personalInfo,
          phoneNumber: formData.personalInfo.phoneNumber,
          email: formData.personalInfo.email || null
        },
        saccoInfo: {
          ...formData.saccoInfo,
        },
        nextOfKin: {
          ...formData.nextOfKin,
          idNumber: formData.nextOfKin.idNumber || null,
          physicalAddress: formData.nextOfKin.physicalAddress || null
        },
        otherNextOfKin: otherNextOfKin.filter(k => k.name.trim())
      };

      const response = await fetch(`${API_URL}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const newErrors = {};
          data.errors.forEach(err => {
            newErrors[err.param || err.field] = err.msg || err.message;
          });
          setErrors(newErrors);
        }
        throw new Error(data.error || 'Failed to create member');
      }

      // Success!
      setCreatedMember(data.data);
      setShowSuccess(true);

    } catch (error) {
      console.error('Submit error:', error);
      alert(error.message || 'Failed to create member. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate age
  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Success modal
  if (showSuccess && createdMember) {
    return (
      <AdminLayout activePage="members" pageTitle="Add New Member" breadcrumbs={[{ label: 'Members', link: '/admin/members' }, { label: 'Add New Member' }]}>
        <div className="add-member-page">
          <div className="success-modal">
            <div className="success-icon-large">✅</div>
            <h1>Member Added Successfully!</h1>
            <div className="success-member-id">{createdMember.memberId}</div>
            <p className="success-member-name">{createdMember.fullName}</p>
            <p className="success-message">
              The member has been registered and is now active in the system.
            </p>

            <div className="success-actions">
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/admin/members/${createdMember.memberId}`)}
              >
                View Member Profile
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSuccess(false);
                  setCreatedMember(null);
                  setCurrentStep(1);
                  setFormData({
                    personalInfo: {
                      fullName: '',
                      idNumber: '',
                      gender: '',
                      phoneNumber: '',
                      email: '',
                      dateOfBirth: '',
                      physicalAddress: '',
                      city: '',
                      homeCounty: '',
                      employmentStatus: '',
                      employerName: ''
                    },
                    saccoInfo: {
                      memberId: '',
                      dateJoined: new Date().toISOString().split('T')[0],
                      membershipType: 'Regular',
                      status: 'Active',
                      department: '',
                      payrollNumber: ''
                    },
                    nextOfKin: {
                      name: '',
                      phoneNumber: '',
                      relationship: '',
                      idNumber: '',
                      physicalAddress: '',
                      benefitPercentage: ''
                    }
                  });
                  setOtherNextOfKin([]);
                  fetchNextMemberId();
                }}
              >
                Add Another Member
              </button>
              <button
                className="btn btn-tertiary"
                onClick={() => navigate('/admin/members')}
              >
                Go to Members List
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activePage="members" pageTitle="Add New Member" breadcrumbs={[{ label: 'Members', link: '/admin/members' }, { label: 'Add New Member' }]}>
      <div className="add-member-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <h1>Add New Member</h1>
            <div className="breadcrumb">
              <span onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer' }}>Dashboard</span>
              <span className="separator">›</span>
              <span onClick={() => navigate('/admin/members')} style={{ cursor: 'pointer' }}>Members</span>
              <span className="separator">›</span>
              <span className="current">Add New Member</span>
            </div>
          </div>

          {/* NEW: header actions with Import + Cancel */}
          <div className="header-actions" style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/admin/members/import')}
              title="Bulk import members from Excel"
            >
              📥 Import Members (Excel)
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => {
                if (window.confirm('Are you sure? Unsaved data will be lost.')) {
                  navigate('/admin/members');
                }
              }}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Progress Stepper */}
        <div className="stepper" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '30px'}}>
          <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div className="step-circle" style={{width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', border: '2px solid', borderColor: currentStep >= 1 ? '#4a90e2' : '#ccc', backgroundColor: currentStep > 1 ? '#4a90e2' : 'white', color: currentStep > 1 ? 'white' : currentStep === 1 ? '#4a90e2' : '#999'}}>
              {currentStep > 1 ? '✔' : '1'}
            </div>
            <div className="step-label" style={{fontSize: '14px', fontWeight: currentStep === 1 ? '600' : '400', color: currentStep >= 1 ? '#333' : '#999'}}>Personal Info</div>
          </div>
          
          <div className="step-line" style={{width: '60px', height: '2px', backgroundColor: currentStep > 1 ? '#4a90e2' : '#ddd'}}></div>
          
          <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div className="step-circle" style={{width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', border: '2px solid', borderColor: currentStep >= 2 ? '#4a90e2' : '#ccc', backgroundColor: currentStep > 2 ? '#4a90e2' : 'white', color: currentStep > 2 ? 'white' : currentStep === 2 ? '#4a90e2' : '#999'}}>
              {currentStep > 2 ? '✔' : '2'}
            </div>
            <div className="step-label" style={{fontSize: '14px', fontWeight: currentStep === 2 ? '600' : '400', color: currentStep >= 2 ? '#333' : '#999'}}>SACCO Info</div>
          </div>
          
          <div className="step-line" style={{width: '60px', height: '2px', backgroundColor: currentStep > 2 ? '#4a90e2' : '#ddd'}}></div>
          
          <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div className="step-circle" style={{width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', border: '2px solid', borderColor: currentStep >= 3 ? '#4a90e2' : '#ccc', backgroundColor: currentStep > 3 ? '#4a90e2' : 'white', color: currentStep > 3 ? 'white' : currentStep === 3 ? '#4a90e2' : '#999'}}>
              {currentStep > 3 ? '✔' : '3'}
            </div>
            <div className="step-label" style={{fontSize: '14px', fontWeight: currentStep === 3 ? '600' : '400', color: currentStep >= 3 ? '#333' : '#999'}}>Next of Kin</div>
          </div>
          
          <div className="step-line" style={{width: '60px', height: '2px', backgroundColor: currentStep > 3 ? '#4a90e2' : '#ddd'}}></div>
          
          <div className={`step ${currentStep >= 4 ? 'active' : ''}`} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div className="step-circle" style={{width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', border: '2px solid', borderColor: currentStep >= 4 ? '#4a90e2' : '#ccc', backgroundColor: 'white', color: currentStep === 4 ? '#4a90e2' : '#999'}}>4</div>
            <div className="step-label" style={{fontSize: '14px', fontWeight: currentStep === 4 ? '600' : '400', color: currentStep >= 4 ? '#333' : '#999'}}>Review</div>
          </div>
        </div>

        {/* Form Container */}
        <div className="form-container">
          {/* STEP 1: Personal Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <h2>Personal Information</h2>

              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.personalInfo.fullName}
                    onChange={(e) => handleChange('personalInfo', 'fullName', e.target.value)}
                    placeholder="Enter full legal name"
                    className={errors['personalInfo.fullName'] ? 'input-error' : ''}
                  />
                  {errors['personalInfo.fullName'] && (
                    <span className="error-message">{errors['personalInfo.fullName']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>ID Number <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.personalInfo.idNumber}
                    onChange={(e) => handleChange('personalInfo', 'idNumber', e.target.value)}
                    placeholder="National ID or Passport"
                    className={errors['personalInfo.idNumber'] ? 'input-error' : ''}
                  />
                  {errors['personalInfo.idNumber'] && (
                    <span className="error-message">{errors['personalInfo.idNumber']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.personalInfo.phoneNumber}
                    onChange={(e) => handleChange('personalInfo', 'phoneNumber', e.target.value)}
                    placeholder="+254 712 345 678"
                    className={errors['personalInfo.phoneNumber'] ? 'input-error' : ''}
                  />
                  {errors['personalInfo.phoneNumber'] && (
                    <span className="error-message">{errors['personalInfo.phoneNumber']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={formData.personalInfo.email}
                    onChange={(e) => handleChange('personalInfo', 'email', e.target.value)}
                    placeholder="member@example.com (Optional)"
                    className={errors['personalInfo.email'] ? 'input-error' : ''}
                  />
                  {errors['personalInfo.email'] && (
                    <span className="error-message">{errors['personalInfo.email']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Date of Birth <span className="required">*</span></label>
                  <input
                    type="date"
                    value={formData.personalInfo.dateOfBirth}
                    onChange={(e) => handleChange('personalInfo', 'dateOfBirth', e.target.value)}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    className={errors['personalInfo.dateOfBirth'] ? 'input-error' : ''}
                  />
                  {errors['personalInfo.dateOfBirth'] && (
                    <span className="error-message">{errors['personalInfo.dateOfBirth']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Employment Area <span className="required">*</span></label>
                  <select
                    value={formData.personalInfo.employmentStatus}
                    onChange={(e) => handleChange('personalInfo', 'employmentStatus', e.target.value)}
                    className={errors['personalInfo.employmentStatus'] ? 'input-error' : ''}
                  >
                    <option value="">Select employment area</option>
                    <option value="Senior School">Senior School</option>
                    <option value="Prep School">Prep School</option>
                    <option value="Work Force">Work Force</option>
                    <option value="Alumni">Alumni</option>
                  </select>
                  {errors['personalInfo.employmentStatus'] && (
                    <span className="error-message">{errors['personalInfo.employmentStatus']}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Physical Address <span className="required">*</span></label>
                <textarea
                  value={formData.personalInfo.physicalAddress}
                  onChange={(e) => handleChange('personalInfo', 'physicalAddress', e.target.value)}
                  placeholder="Full physical address"
                  rows="3"
                  className={errors['personalInfo.physicalAddress'] ? 'input-error' : ''}
                />
                {errors['personalInfo.physicalAddress'] && (
                  <span className="error-message">{errors['personalInfo.physicalAddress']}</span>
                )}
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    value={formData.personalInfo.gender}
                    onChange={(e) => handleChange('personalInfo', 'gender', e.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>City / Town</label>
                  <input
                    type="text"
                    value={formData.personalInfo.city}
                    onChange={(e) => handleChange('personalInfo', 'city', e.target.value)}
                    placeholder="e.g. Nakuru"
                  />
                </div>

                <div className="form-group">
                  <label>Home County</label>
                  <input
                    type="text"
                    value={formData.personalInfo.homeCounty}
                    onChange={(e) => handleChange('personalInfo', 'homeCounty', e.target.value)}
                    placeholder="e.g. Nakuru"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: SACCO Information */}
          {currentStep === 2 && (
            <div className="form-step">
              <h2>SACCO Information</h2>

              <div className="form-grid">
                <div className="form-group">
                  <label>Member ID <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.saccoInfo.memberId}
                    onChange={(e) => handleChange('saccoInfo', 'memberId', e.target.value.toUpperCase())}
                    placeholder="e.g., S00001"
                    className={errors['saccoInfo.memberId'] ? 'input-error' : ''}
                  />
                  <p className="help-text">Enter unique member ID (e.g., S00001, S00002)</p>
                  {errors['saccoInfo.memberId'] && (
                    <span className="error-message">{errors['saccoInfo.memberId']}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Date Joined <span className="required">*</span></label>
                  <input
                    type="date"
                    value={formData.saccoInfo.dateJoined}
                    onChange={(e) => handleChange('saccoInfo', 'dateJoined', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className={errors['saccoInfo.dateJoined'] ? 'input-error' : ''}
                  />
                  {errors['saccoInfo.dateJoined'] && (
                    <span className="error-message">{errors['saccoInfo.dateJoined']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Membership Type <span className="required">*</span></label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="Regular"
                        checked={formData.saccoInfo.membershipType === 'Regular'}
                        onChange={(e) => handleChange('saccoInfo', 'membershipType', e.target.value)}
                      />
                      <span>Regular</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="Associate"
                        checked={formData.saccoInfo.membershipType === 'Associate'}
                        onChange={(e) => handleChange('saccoInfo', 'membershipType', e.target.value)}
                      />
                      <span>Associate</span>
                    </label>
                  </div>
                  <p className="help-text">Regular members have full voting rights</p>
                </div>

                <div className="form-group">
                  <label>Status <span className="required">*</span></label>
                  <select
                    value={formData.saccoInfo.status}
                    onChange={(e) => handleChange('saccoInfo', 'status', e.target.value)}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <p className="help-text">New members are usually Active</p>
                </div>

                <div className="form-group">
                  <label>Department</label>
                  <select
                    value={formData.saccoInfo.department}
                    onChange={(e) => handleChange('saccoInfo', 'department', e.target.value)}
                  >
                    <option value="">Select department</option>
                    <option value="Senior School">Senior School</option>
                    <option value="Prep School">Prep School</option>
                    <option value="Work Force">Work Force</option>
                    <option value="Alumni">Alumni</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Payroll Number</label>
                  <input
                    type="text"
                    value={formData.saccoInfo.payrollNumber}
                    onChange={(e) => handleChange('saccoInfo', 'payrollNumber', e.target.value)}
                    placeholder="e.g. 696"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Next of Kin */}
          {currentStep === 3 && (
            <div className="form-step">
              <h2>Next of Kin Information</h2>

              <div className="info-box">
                <span className="info-icon">ℹ️</span>
                <p>Your Next of Kin will be contacted in case of emergencies or as a beneficiary</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.nextOfKin.name}
                    onChange={(e) => handleChange('nextOfKin', 'name', e.target.value)}
                    placeholder="Full legal name"
                    className={errors['nextOfKin.name'] ? 'input-error' : ''}
                  />
                  {errors['nextOfKin.name'] && (
                    <span className="error-message">{errors['nextOfKin.name']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Relationship <span className="required">*</span></label>
                  <select
                    value={formData.nextOfKin.relationship}
                    onChange={(e) => handleChange('nextOfKin', 'relationship', e.target.value)}
                    className={errors['nextOfKin.relationship'] ? 'input-error' : ''}
                  >
                    <option value="">Select relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Child">Child</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors['nextOfKin.relationship'] && (
                    <span className="error-message">{errors['nextOfKin.relationship']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.nextOfKin.phoneNumber}
                    onChange={(e) => handleChange('nextOfKin', 'phoneNumber', e.target.value)}
                    placeholder="+254 722 222 222"
                    className={errors['nextOfKin.phoneNumber'] ? 'input-error' : ''}
                  />
                  {errors['nextOfKin.phoneNumber'] && (
                    <span className="error-message">{errors['nextOfKin.phoneNumber']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>ID Number</label>
                  <input
                    type="text"
                    value={formData.nextOfKin.idNumber}
                    onChange={(e) => handleChange('nextOfKin', 'idNumber', e.target.value)}
                    placeholder="National ID or Passport (Optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Benefit Percentage (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.nextOfKin.benefitPercentage}
                    onChange={(e) => handleChange('nextOfKin', 'benefitPercentage', e.target.value)}
                    placeholder="e.g. 50"
                  />
                  <p className="help-text">Share of assets this person inherits (Optional)</p>
                </div>
              </div>

              <div className="form-group">
                <label>Physical Address</label>
                <textarea
                  value={formData.nextOfKin.physicalAddress}
                  onChange={(e) => handleChange('nextOfKin', 'physicalAddress', e.target.value)}
                  placeholder="Next of kin's address (Optional)"
                  rows="2"
                />
              </div>

              {/* Other Next of Kin */}
              <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>Other Next of Kin (Additional Beneficiaries)</h3>
                  <button type="button" className="btn btn-secondary" onClick={addOtherKin} style={{ fontSize: 13 }}>
                    + Add Another
                  </button>
                </div>
                {otherNextOfKin.length === 0 && (
                  <p style={{ color: '#888', fontSize: 13 }}>No additional beneficiaries added.</p>
                )}
                {otherNextOfKin.map((kin, idx) => (
                  <div key={idx} style={{ border: '1px solid #ddd', borderRadius: 6, padding: '12px 16px', marginBottom: 10, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <strong style={{ fontSize: 13 }}>Beneficiary {idx + 1}</strong>
                      <button type="button" onClick={() => removeOtherKin(idx)} style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 13 }}>✕ Remove</button>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Full Name</label>
                        <input
                          type="text"
                          value={kin.name}
                          onChange={(e) => handleOtherKinChange(idx, 'name', e.target.value)}
                          placeholder="Full name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Relationship</label>
                        <select value={kin.relationship} onChange={(e) => handleOtherKinChange(idx, 'relationship', e.target.value)}>
                          <option value="">Select</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Parent">Parent</option>
                          <option value="Child">Child</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Benefit Percentage (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={kin.benefitPercentage}
                          onChange={(e) => handleOtherKinChange(idx, 'benefitPercentage', e.target.value)}
                          placeholder="e.g. 25"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: Review & Submit */}
          {currentStep === 4 && (
            <div className="form-step">
              <h2>Review & Submit</h2>

              <div className="warning-box">
                <span className="warning-icon">⚠️</span>
                <p>Please review all information carefully. Once submitted, some details cannot be changed.</p>
              </div>

              <div className="review-section">
                <div className="review-card">
                  <div className="review-header">
                    <h3>Personal Information</h3>
                    <button className="btn-edit" onClick={() => setCurrentStep(1)}>Edit</button>
                  </div>
                  <div className="review-content">
                    <div className="review-item">
                      <span className="review-label">Full Name:</span>
                      <span className="review-value">{formData.personalInfo.fullName}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">ID Number:</span>
                      <span className="review-value">{formData.personalInfo.idNumber}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Phone Number:</span>
                      <span className="review-value">{formData.personalInfo.phoneNumber}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Email:</span>
                      <span className="review-value">{formData.personalInfo.email || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Date of Birth:</span>
                      <span className="review-value">
                        {formData.personalInfo.dateOfBirth} ({calculateAge(formData.personalInfo.dateOfBirth)} years old)
                      </span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Address:</span>
                      <span className="review-value">{formData.personalInfo.physicalAddress}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Gender:</span>
                      <span className="review-value">{formData.personalInfo.gender || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">City / Town:</span>
                      <span className="review-value">{formData.personalInfo.city || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Home County:</span>
                      <span className="review-value">{formData.personalInfo.homeCounty || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Employment Area:</span>
                      <span className="review-value">{formData.personalInfo.employmentStatus}</span>
                    </div>
                  </div>
                </div>

                <div className="review-card">
                  <div className="review-header">
                    <h3>SACCO Information</h3>
                    <button className="btn-edit" onClick={() => setCurrentStep(2)}>Edit</button>
                  </div>
                  <div className="review-content">
                    <div className="review-item">
                      <span className="review-label">Member ID:</span>
                      <span className="review-value member-id-highlight">{formData.saccoInfo.memberId}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Date Joined:</span>
                      <span className="review-value">{formData.saccoInfo.dateJoined}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Membership Type:</span>
                      <span className="review-value">{formData.saccoInfo.membershipType}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Status:</span>
                      <span className={`status-badge status-${formData.saccoInfo.status.toLowerCase()}`}>
                        {formData.saccoInfo.status}
                      </span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Department:</span>
                      <span className="review-value">{formData.saccoInfo.department || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Payroll Number:</span>
                      <span className="review-value">{formData.saccoInfo.payrollNumber || 'Not provided'}</span>
                    </div>
                  </div>
                </div>

                <div className="review-card">
                  <div className="review-header">
                    <h3>Next of Kin</h3>
                    <button className="btn-edit" onClick={() => setCurrentStep(3)}>Edit</button>
                  </div>
                  <div className="review-content">
                    <div className="review-item">
                      <span className="review-label">Full Name:</span>
                      <span className="review-value">{formData.nextOfKin.name}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Relationship:</span>
                      <span className="review-value">{formData.nextOfKin.relationship}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Phone Number:</span>
                      <span className="review-value">{formData.nextOfKin.phoneNumber}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">ID Number:</span>
                      <span className="review-value">{formData.nextOfKin.idNumber || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Address:</span>
                      <span className="review-value">{formData.nextOfKin.physicalAddress || 'Not provided'}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Benefit %:</span>
                      <span className="review-value">{formData.nextOfKin.benefitPercentage ? `${formData.nextOfKin.benefitPercentage}%` : 'Not provided'}</span>
                    </div>
                    {otherNextOfKin.filter(k => k.name.trim()).length > 0 && (
                      <div className="review-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span className="review-label" style={{ marginBottom: 4 }}>Other Beneficiaries:</span>
                        {otherNextOfKin.filter(k => k.name.trim()).map((k, i) => (
                          <span key={i} className="review-value" style={{ marginBottom: 2 }}>
                            {k.name} ({k.relationship || '—'}) — {k.benefitPercentage ? `${k.benefitPercentage}%` : 'no %'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="form-actions">
            {currentStep > 1 && (
              <button className="btn btn-secondary" onClick={handleBack}>
                Back
              </button>
            )}

            {currentStep < 4 ? (
              <button className="btn btn-primary" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button 
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-small"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddMemberPage;